export type NetworkQualityLevel = 1 | 2 | 3 | 4 | 5;
export type QualityLimitationReason = "none" | "cpu" | "bandwidth" | "other";

export interface RawNetworkSample {
  rtt: number;
  jitter: number;
  bitrate: number;
  jitterBufferDelay: number;
  qualityLimitationReason: QualityLimitationReason;
  freezeCount: number;
  framesDropped: number;
  packetsLost: number;
  packetsReceived: number;
}

export interface SampleDelta {
  packetLoss: number;
}

export function reduceRtcStats(rtcStats: any[]): RawNetworkSample {
  let rtt = 0;
  let jitter = 0;
  let bitrate = 0;
  let packetsLost = 0;
  let packetsReceived = 0;
  let jitterBufferDelay = 0;
  let jitterBufferEmittedCount = 0;
  let qualityLimitationReason: QualityLimitationReason = "none";
  let freezeCount = 0;
  let framesDropped = 0;

  rtcStats.forEach((report: any) => {
    if (report.type === "candidate-pair" && report.state === "succeeded") {
      if (report.currentRoundTripTime) rtt = report.currentRoundTripTime * 1000;
      if (report.availableOutgoingBitrate) bitrate = report.availableOutgoingBitrate;
    } else if (report.type === "inbound-rtp") {
      packetsLost += report.packetsLost || 0;
      packetsReceived += report.packetsReceived || 0;
      if (report.kind === "video") {
        jitter = Math.max(jitter, (report.jitter || 0) * 1000);
        jitterBufferDelay += report.jitterBufferDelay || 0;
        jitterBufferEmittedCount += report.jitterBufferEmittedCount || 0;
        freezeCount += report.freezeCount || 0;
        framesDropped += report.framesDropped || 0;
      } else if (report.kind === "audio") {
        jitter = Math.max(jitter, (report.jitter || 0) * 1000);
        jitterBufferDelay += report.jitterBufferDelay || 0;
        jitterBufferEmittedCount += report.jitterBufferEmittedCount || 0;
      }
    } else if (report.type === "outbound-rtp") {
      packetsReceived += report.packetsSent || 0;
      if (report.qualityLimitationReason && report.qualityLimitationReason !== "none") {
        qualityLimitationReason = report.qualityLimitationReason as QualityLimitationReason;
      }
    } else if (report.type === "remote-inbound-rtp") {
      packetsLost += report.packetsLost || 0;
      jitter = Math.max(jitter, (report.jitter || 0) * 1000);
      if (report.roundTripTime && !rtt) rtt = report.roundTripTime * 1000;
    }
  });

  // Per WebRTC stats spec: jitterBufferDelay is a sum-of-delays in seconds,
  // jitterBufferEmittedCount is the number of samples that exited the
  // buffer. The mean-per-sample delay is the actual buffer dwell time —
  // this is the metric that goes to seconds when audio "lags by 5-10 s".
  const meanJitterBufferDelayMs = jitterBufferEmittedCount > 0
    ? (jitterBufferDelay / jitterBufferEmittedCount) * 1000
    : 0;

  return {
    rtt,
    jitter,
    bitrate,
    jitterBufferDelay: meanJitterBufferDelayMs,
    qualityLimitationReason,
    freezeCount,
    framesDropped,
    packetsLost,
    packetsReceived,
  };
}

export function sampleDelta(curr: RawNetworkSample, prev: RawNetworkSample | null): SampleDelta {
  if (!prev) return { packetLoss: 0 };
  const lostDelta = Math.max(0, curr.packetsLost - prev.packetsLost);
  const receivedDelta = Math.max(0, curr.packetsReceived - prev.packetsReceived);
  const total = lostDelta + receivedDelta;
  if (total <= 0) return { packetLoss: 0 };
  return { packetLoss: (lostDelta / total) * 100 };
}

interface ScoreInputs {
  rtt: number;
  packetLoss: number;
  jitter: number;
  jitterBufferDelay: number;
  qualityLimitationReason: QualityLimitationReason;
}

// Buffer-delay clause exists specifically to catch the "voice lags 5-10 s"
// symptom: when NetEQ stretches the audio jitter buffer past ~1 s the call
// is unusable regardless of RTT, so it forces quality=1 immediately.
export function qualityFromSample(inputs: ScoreInputs, _delta: SampleDelta): NetworkQualityLevel {
  const { rtt, packetLoss, jitter, jitterBufferDelay, qualityLimitationReason } = inputs;

  if (jitterBufferDelay > 1000) return 1;
  if (jitterBufferDelay > 500) return Math.min(2, scoreFromBaseline(rtt, packetLoss, jitter)) as NetworkQualityLevel;

  let level = scoreFromBaseline(rtt, packetLoss, jitter);
  if (qualityLimitationReason === "cpu" || qualityLimitationReason === "bandwidth") {
    level = Math.min(level, 2) as NetworkQualityLevel;
  }
  return level;
}

function scoreFromBaseline(rtt: number, packetLoss: number, jitter: number): NetworkQualityLevel {
  if (rtt < 80 && packetLoss < 1 && jitter < 15) return 5;
  if (rtt < 150 && packetLoss < 2 && jitter < 25) return 4;
  if (rtt < 250 && packetLoss < 5 && jitter < 50) return 3;
  if (rtt < 400 && packetLoss < 10 && jitter < 80) return 2;
  return 1;
}
